/* eslint-disable no-param-reassign */
import convert from 'corva-convert-units';
import { mapValues, sortBy, uniqBy } from 'lodash';

import {
  IMPERIAL_UNITS,
  METRIC_UNITS,
  UNIT_DEFINITIONS,
  UNIT_TYPES_DEFINITIONS_MAP,
} from './constants/units';

const DEFAULT_PRECISION = 2;
const UNIT_SYSTEM_TYPES = {
  custom: 'custom',
  default: 'imperial',
};

const units = {
  imperial: IMPERIAL_UNITS,
  metric: METRIC_UNITS,
  custom: { ...IMPERIAL_UNITS },
};

const unitSystem = UNIT_SYSTEM_TYPES.custom;
let userUnitsUpdatesSubscribers = [];
let customUnitDefinitions = {};

export const getAllUnitTypes = () => UNIT_DEFINITIONS;

export const getDefaultUnits = () => units[unitSystem];

const normalizeCompanyCustomUnits = customUnits =>
  mapValues(customUnits || {}, ({ measures, name }, type) => ({
    display: name,
    imperial: type,
    measures: measures.map(measure => ({
      abbr: measure.abbr,
      display: measure.name,
      measure: measure.abbr,
      plural: measure.name,
      singular: measure.name,
      system: 'custom',
    })),
    metric: type,
    origin: type,
    type,
  }));

/**
 * Adds platform notification toast
 * @param {object} newUnits
 */
function notifyAboutUserUnitsUpdate(newUnits) {
  userUnitsUpdatesSubscribers.forEach(callback => callback(newUnits));
}

/**
 * Updates current custom unit system with user data
 * @param {object} userData
 * @param {Record<string, string>} userData.userUnits
 * @param {Record<string, string>} userData.companyUnits
 */
export const updateUserUnits = ({
  userUnits = {},
  companyUnits = {},
  companyCustomUnitSystem = {},
  userCustomUnitSystem = {},
  companyCustomUnits,
}) => {
  units.custom = {
    ...IMPERIAL_UNITS,
    ...companyUnits,
    ...companyCustomUnitSystem,
    ...userUnits,
    ...userCustomUnitSystem,
  };

  if (companyCustomUnits) {
    customUnitDefinitions = normalizeCompanyCustomUnits(companyCustomUnits);
  }

  notifyAboutUserUnitsUpdate(units.custom);
};

export function getUserUnits() {
  return units.custom;
}

/**
 * Used for DC Isolated App container
 * @param  {Function} newCallback
 */
export function subscribeForUserUnitsUpdates(newCallback) {
  userUnitsUpdatesSubscribers.push(newCallback);

  return () => {
    userUnitsUpdatesSubscribers = userUnitsUpdatesSubscribers.filter(
      callback => callback !== newCallback
    );
  };
}

/**
 * Retrieves a unit of a given type by current unit system
 * @param {string} unitType length, mass, volume, etc
 * @returns {string}
 */
export const getUnitPreference = unitType => units[unitSystem][unitType];

/**
 * Lists all the units of a specific type
 * @param {string} type
 * @returns {Object[]}
 */
export const getUnitsByType = type => {
  const definition = UNIT_TYPES_DEFINITIONS_MAP[type];

  if (definition) {
    return sortBy(convert().list(definition.origin), 'abbr');
  }
  // NOTE: Look up in custom company units
  return customUnitDefinitions[type]?.measures || [];
};

/**
 * Returns unique units list for some measurements that use same units for imperial and metric system
 * @param {string} type
 * @returns {Object[]}
 */
export const getUniqueUnitsByType = type => {
  const definition = getUnitsByType(type);

  return uniqBy(definition, 'abbr');
};

/**
 * @deprecated - unit system can be custom, better to use convert functions
 */
export const getUnitSystem = () => unitSystem;

/**
 * Retrieves the unit display name for a given unit type
 * @param {string} unitType length, mass, volume, etc
 * @param {string} [unitTo=null]
 * @returns {string}
 */
export const getUnitDisplay = (unitType, unitTo = null) => {
  const measure = UNIT_TYPES_DEFINITIONS_MAP[unitType]?.origin;
  const convertedValue = convert(undefined, measure);
  if (unitTo === null) {
    unitTo = units.custom[unitType];
  }
  if (convertedValue.getUnit(unitTo)) {
    return convertedValue.describe(unitTo).display;
  }

  const customUnitDisplay = getUnitsByType(unitType).find(
    ({ measure }) => measure === unitTo
  )?.display;

  return customUnitDisplay || unitTo;
};

/**
 * @deprecated - should not depends on imperial system only, because we have mixed unit system
 * Retrieves the unit display name for imperial unit type
 * @param unitType length, mass, volume, etc
 * @returns string
 */
export const getDefaultImperialUnit = unitType => units.imperial[unitType];

/**
 * Converts a single value
 * @param {number} value The value we want converted.
 * @param {string} unitType The class of unit such as volume, length, mass, etc.
 * @param {string} from The specific unit such as m, gal, lb, etc.
 * @param {string} to (optional) the unit that we want to convert the value to.
 * @param {number} precision to round to the value
 * May be passed in my ConvertList
 * @return {number}
 */
export const convertValue = (value, unitType, from, to = null, precision = DEFAULT_PRECISION) => {
  if (!value || !from || from === to || !!customUnitDefinitions[unitType]) {
    return value;
  }

  if (to === null) {
    to = getUnitPreference(unitType);
    if (!to || from === to) {
      return value;
    }
  }

  const measure = UNIT_TYPES_DEFINITIONS_MAP[unitType]?.origin;
  const convertedValue = convert(value, measure).from(from).to(to);

  return Number.isFinite(convertedValue)
    ? Math.round(convertedValue * 10 ** precision) / 10 ** precision
    : convertedValue;
};

/**
 * Converts a key in an immutable list of immutables.
 * @param {*} immt The list of maps/lists containing values that we want to convert
 * @param {string} key The key in each sub-iterable that we want to convert
 * @param {string} unitType The class of unit such as volume, length, mass, etc.
 * @param {string} from The specific unit such as m, gal, lb, etc.
 * @param {string} to (optional) the unit that we want to convert the value to.
 * @param {number} precision to round to the value
 * @returns {*} Immutable
 */
export const convertImmutables = (
  immt,
  key,
  unitType,
  from,
  to = null,
  precision = DEFAULT_PRECISION
) =>
  immt.map(currentUnitMap => {
    const convertedValue = convertValue(currentUnitMap.get(key), unitType, from, to, precision);
    return currentUnitMap.set(key, convertedValue);
  });

/**
 * convertImmutablesByBatch is the expansion of convertImmutables
 * It allows you to convert the immutables by multiple keys/units at once.
 *
 * @param {*} immt immutable you want to convert
 * @param {*} conversionRules is the array of conversion to be done { key, unitType, from, to }
 */
export const convertImmutablesByBatch = (immt, conversionRules) =>
  immt.map(item =>
    conversionRules.reduce((convertingItem, conversionRule) => {
      const { key, unitType, from, to, precision } = conversionRule;
      const val = convertingItem.get(key);
      const convertedValue = convertValue(val, unitType, from, to, precision);
      return convertingItem.set(key, convertedValue);
    }, item)
  );

/**
 * Converts a property in a simple array of js objects or arrays.
 * @param {number[][]} iterable The array of iterables containing values that we want to convert
 * @param {string} key The key in each sub-element that we want to convert
 * @param {string} unitType The class of unit such as volume, length, mass, etc.
 * @param {string} from The specific unit such as m, gal, lb, etc.
 * @param {string} to (optional) the unit that we want to convert the value to.
 * @returns {number[][]}
 */
export const convertArray = (
  iterable = [],
  key,
  unitType,
  from,
  to = null,
  precision = DEFAULT_PRECISION
) => {
  for (let i = 0; i < iterable.length; i += 1) {
    iterable[i][key] = convertValue(iterable[i][key], unitType, from, to, precision);
  }

  return iterable;
};

/**
 * Retrieves the full name for a given unit type, singular
 * @param {string} unitType length, mass, volume, etc
 * @returns {string}
 */
export const getUnitSingular = unitType => {
  const measure = UNIT_TYPES_DEFINITIONS_MAP[unitType]?.origin;
  if (customUnitDefinitions[unitType]) {
    return getUnitDisplay(unitType);
  }

  return convert(undefined, measure).describe(getUnitPreference(unitType)).singular;
};

/**
 * Retrieves the full name for a given unit type, plural
 * @param {string} unitType length, mass, volume, etc
 * @returns {string}
 */
export const getUnitPlural = unitType => {
  const measure = UNIT_TYPES_DEFINITIONS_MAP[unitType]?.origin;
  if (customUnitDefinitions[unitType]) {
    return getUnitDisplay(unitType);
  }

  return convert(undefined, measure).describe(getUnitPreference(unitType)).plural;
};

/**
 * Retrieves unit data
 *
 * @param {string} unitType length, mass, volume, etc
 * @return {{
 *    abbr: string,
 *    measure: string,
 *    system: string,
 *    singular: string,
 *    plural: string,
 *    display: string
 * }}
 */
export const getUnitDescription = unitType => {
  const measure = UNIT_TYPES_DEFINITIONS_MAP[unitType]?.origin;

  return convert(undefined, measure).describe(unitType);
};
